import { HOME_ASSETS } from "@/home/home-assets";
import {
  DUNGEON_GOLD_PER_EXP,
  type DungeonBattleRewardTotals,
} from "@/dungeon/dungeon-battle-rewards";

export type DungeonBattleResultView = {
  floor: number;
  victory: boolean;
  trial: boolean;
  baseXp: number;
  monstersKilled: number;
  rewards: DungeonBattleRewardTotals;
  primaryLabel: string;
  secondaryLabel?: string;
};

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function formatNum(n: number): string {
  return n.toLocaleString("en-US");
}

export function showDungeonBattleResult(
  host: HTMLElement,
  view: DungeonBattleResultView,
): Promise<"primary" | "secondary"> {
  return new Promise((resolve) => {
    const existing = host.querySelector(".dungeon-battle-result");
    existing?.remove();

    const overlay = document.createElement("div");
    overlay.className = "dungeon-battle-result";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "สรุปการต่อสู้");

    const { rewards, trial, victory, baseXp, monstersKilled } = view;
    const title = victory ? `ด่าน ${view.floor}` : "จบการต่อสู้";
    const modeBadge = trial
      ? `<span class="dungeon-battle-result-mode dungeon-battle-result-mode--trial">ทดลอง</span>`
      : `<span class="dungeon-battle-result-mode dungeon-battle-result-mode--raid">โจมตี</span>`;
    const repeatBadge =
      !trial && rewards.repeatClear
        ? `<span class="dungeon-battle-result-mode dungeon-battle-result-mode--repeat">รางวัล ×70%</span>`
        : "";

    const expDisplay = trial ? baseXp : rewards.exp;
    const goldDisplay = trial
      ? Math.floor(baseXp * DUNGEON_GOLD_PER_EXP)
      : rewards.gold;
    const previewClass = trial ? " dungeon-battle-result-loot-value--preview" : "";

    overlay.innerHTML = `
      <div class="dungeon-battle-result-backdrop" aria-hidden="true"></div>
      <div class="dungeon-battle-result-card ${victory ? "dungeon-battle-result-card--victory" : ""}">
        <div class="dungeon-battle-result-shine" aria-hidden="true"></div>
        <header class="dungeon-battle-result-header">
          <p class="dungeon-battle-result-kicker">${victory ? "VICTORY" : "RESULT"}</p>
          <h2 class="dungeon-battle-result-title">${escapeHtml(title)}</h2>
          <div class="dungeon-battle-result-badges">
            ${modeBadge}
            ${repeatBadge}
          </div>
        </header>

        <div class="dungeon-battle-result-stats" role="list">
          <div class="dungeon-battle-result-stat" role="listitem">
            <span class="dungeon-battle-result-stat-icon" aria-hidden="true">
              <img src="${HOME_ASSETS.shopTabs.monster}" width="28" height="28" alt="" />
            </span>
            <span class="dungeon-battle-result-stat-body">
              <span class="dungeon-battle-result-stat-label">มอนสเตอร์ที่กำจัด</span>
              <span class="dungeon-battle-result-stat-value">${formatNum(monstersKilled)}</span>
            </span>
          </div>
          <div class="dungeon-battle-result-stat" role="listitem">
            <span class="dungeon-battle-result-stat-icon dungeon-battle-result-stat-icon--xp" aria-hidden="true">EXP</span>
            <span class="dungeon-battle-result-stat-body">
              <span class="dungeon-battle-result-stat-label">พลังโจมตี (ฐาน)</span>
              <span class="dungeon-battle-result-stat-value">${formatNum(baseXp)}</span>
            </span>
          </div>
        </div>

        <section class="dungeon-battle-result-loot" aria-label="รางวัล">
          <p class="dungeon-battle-result-loot-title">${trial ? "รางวัลถ้าโจมตีจริง" : "ได้รับ"}</p>
          <div class="dungeon-battle-result-loot-row">
            <div class="dungeon-battle-result-loot-chip">
              <span class="dungeon-battle-result-loot-icon-wrap" aria-hidden="true">
                <span class="dungeon-battle-result-loot-icon dungeon-battle-result-loot-icon--exp">★</span>
              </span>
              <span class="dungeon-battle-result-loot-chip-body">
                <span class="dungeon-battle-result-loot-value${previewClass}">+${formatNum(expDisplay)}</span>
                <span class="dungeon-battle-result-loot-unit">EXP</span>
              </span>
            </div>
            <div class="dungeon-battle-result-loot-chip">
              <span class="dungeon-battle-result-loot-icon-wrap" aria-hidden="true">
                <img class="dungeon-battle-result-loot-icon-img" src="${HOME_ASSETS.coin}" width="22" height="22" alt="" />
              </span>
              <span class="dungeon-battle-result-loot-chip-body">
                <span class="dungeon-battle-result-loot-value${previewClass}">+${formatNum(goldDisplay)}</span>
                <span class="dungeon-battle-result-loot-unit">ทอง</span>
              </span>
            </div>
          </div>
          ${
            trial
              ? `<p class="dungeon-battle-result-loot-hint">โหมดทดลองไม่บันทึกรางวัล</p>`
              : ""
          }
        </section>

        <div class="dungeon-battle-result-actions">
          ${
            view.secondaryLabel
              ? `<button type="button" class="dungeon-battle-result-btn dungeon-battle-result-btn--ghost" data-dungeon-result-secondary>${escapeHtml(view.secondaryLabel)}</button>`
              : ""
          }
          <button type="button" class="dungeon-battle-result-btn dungeon-battle-result-btn--primary" data-dungeon-result-primary>${escapeHtml(view.primaryLabel)}</button>
        </div>
      </div>
    `;

    const done = (choice: "primary" | "secondary") => {
      overlay.remove();
      resolve(choice);
    };

    overlay.querySelector("[data-dungeon-result-primary]")?.addEventListener("click", () => {
      done("primary");
    });
    overlay.querySelector("[data-dungeon-result-secondary]")?.addEventListener("click", () => {
      done("secondary");
    });

    host.appendChild(overlay);
  });
}
